import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ProjectHistoryItem {
  id: number;
  project_id: string;
  status?: string;
  /** Phase du pipeline — reflet de Transaction.phase_transaction (lecture seule). */
  phase?: string | null;
  /** Résultat d'appel de la transaction liée : OK | NRP, null = jamais appelé. */
  resultat_appel?: string | null;
  tarif_mensuel_moins_cher?: number | null;
  cotisation_totale_moins_chere?: number | null;
  assureur_moins_cher?: string | null;
  user_id?: number;
  user_email?: string;
  user_name?: string;
  created_at: string;
  date_traitement?: string | null;
  
  // Informations générales
  type_souscripteur?: string;
  type_projet?: string;
  banque?: string;
  origine?: string;
  projet_source_id?: string;
  raison_sociale?: string;
  forme_juridique?: string;
  nom_projet?: string;
  commentaire?: string;
  commentaire_emprunteur?: string;
  commentaire_pret?: string;
  commentaire_garanties?: string;
  
  // Emprunteur principal
  civilite?: string;
  nom?: string;
  prenom?: string;
  adresse?: string;
  profession?: string;
  complement_adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
  telephone?: string;
  email?: string;
  email_is_valid?: boolean | null;
  date_naissance?: string;
  date_effective?: string;
  fumeur?: boolean;
  deplacement_professionnel?: string | boolean;
  port_charge?: boolean;
  travaille_en_hauteur?: string | boolean;
  en_cours_total_assure?: number;
  professional_category?: string;
  
  // Co-emprunteur
  est_co_emprunteur?: boolean;
  co_emprunteur_civilite?: string;
  co_emprunteur_nom?: string;
  co_emprunteur_prenom?: string;
  co_emprunteur_email?: string;
  co_emprunteur_date_naissance?: string;
  co_emprunteur_fumeur?: boolean;
  co_emprunteur_profession?: string;
  co_emprunteur_deplacement_professionnel?: string | boolean;
  co_emprunteur_port_charge?: boolean;
  co_emprunteur_travail_en_hauteur?: string | boolean;
  co_emprunteur_en_cours_total_assure?: number;
  co_emprunteur_professional_category?: string;
  
  // Prêt principal
  type_pret?: string;
  montant?: number;
  taux?: number;
  duree?: number;
  differe?: string;
  type_differe?: string;
  duree_palier?: number;
  montant_echeance_palier?: number;
  montant_echeance?: number;
  paliers?: any[] | string;
  prets_additionnels?: any[] | string;
  
  // Garanties emprunteur principal
  quotite?: number;
  franchise_itt?: string;
  frais_courtage?: string;
  quotite_dc?: boolean;
  quotite_ptia?: boolean;
  quotite_itt?: boolean;
  quotite_ipt?: boolean;
  quotite_ipp?: boolean;
  quotite_ippro?: boolean;
  maux_dos?: boolean;
  psy?: boolean;
  
  // Garanties co-emprunteur
  co_emprunteur_quotite?: number;
  co_emprunteur_franchise_itt?: string;
  co_emprunteur_frais_courtage?: string;
  co_emprunteur_quotite_dc?: boolean;
  co_emprunteur_quotite_ptia?: boolean;
  co_emprunteur_quotite_itt?: boolean;
  co_emprunteur_quotite_ipt?: boolean;
  co_emprunteur_quotite_ipp?: boolean;
  co_emprunteur_quotite_ippro?: boolean;
  co_emprunteur_maux_dos?: boolean;
  co_emprunteur_psy?: boolean;
  
  // Reprise
  type_reprise?: string;
  reprise_avec_delegation?: boolean;
}

export interface ProjectHistoryListItem {
  id: number;
  project_id: string;  // ← Assurez-vous que c'est bien string
  status?: string;
  /** Phase du pipeline — reflet de Transaction.phase_transaction (lecture seule). */
  phase?: string | null;
  /** Résultat d'appel de la transaction liée : OK | NRP, null = jamais appelé. */
  resultat_appel?: string | null;
  tarif_mensuel_moins_cher?: number | null;
  cotisation_totale_moins_chere?: number | null;
  assureur_moins_cher?: string | null;
  user_id?: number;
  user_email?: string;
  user_name?: string;
  created_at: string;
  type_souscripteur: string;
  type_projet: string;
  banque: string;
  origine: string;
  projet_source_id?: string;
  raison_sociale: string;
  nom_projet?: string;
  /** Commentaire de la Transaction liée (même valeur qu'en agenda / transactions). */
  commentaire?: string | null;
  civilite?: string;
  nom: string;
  prenom: string;
  telephone?: string;
  email: string;
  email_is_valid?: boolean | null;
  montant: number;
  duree: number;
  taux: number;
  type_pret: string;
  prets_additionnels?: any[] | string;
  est_co_emprunteur: boolean;
  co_emprunteur_nom: string;
  co_emprunteur_prenom: string;
}

export interface PaginatedProjectResponse {
  items: ProjectHistoryListItem[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface ProjectHistoryFilters {
  search?: string;
  origine?: string;
  type_projet?: string;
  status?: string;
  email_validity?: 'valid' | 'invalid';
  cotisation_level?: 'low' | 'mid' | 'high';
  user_id?: number;
  start_date?: string;
  end_date?: string;
}

export interface ProjectHistoryExportOptions extends ProjectHistoryFilters {
  export_all_matching?: boolean;
}

export interface BulkDeleteProjectsResponse {
  message: string;
  requested_count: number;
  deleted_count: number;
  deleted_project_ids: string[];
  not_found_project_ids: string[];
}

export interface BulkDeleteByMobileExcelResponse {
  message: string;
  requested_count: number;
  unique_mobile_count: number;
  matched_mobile_count: number;
  deleted_count: number;
  deleted_project_ids: string[];
  matched_mobiles: string[];
  not_found_mobiles: string[];
  requested_delete_count?: number;
  unique_delete_phone_count?: number;
  matched_delete_phone_count?: number;
  matched_delete_phones?: string[];
  not_found_delete_phones?: string[];
  requested_clear_count?: number;
  unique_clear_phone_count?: number;
  matched_clear_phone_count?: number;
  phone_cleared_count?: number;
  phone_cleared_project_ids?: string[];
  matched_clear_phones?: string[];
  not_found_clear_phones?: string[];
}

export interface BulkStatusProjectsResponse {
  message: string;
  requested_count: number;
  updated_count: number;
  updated_project_ids: string[];
  not_found_project_ids: string[];
  status: string;
}

export interface ProjectStats {
  total_projects: number;
  monthly_stats: Array<{
    year: number;
    month: number;
    count: number;
  }>;
  project_types: Array<{
    type: string;
    count: number;
  }>;
  top_banks: Array<{
    banque: string;
    count: number;
  }>;
}

export interface ProjectUserStatusCounts {
  user_id?: number | null;
  user_email?: string | null;
  user_name?: string | null;
  total_projects: number;
  a_traiter_count: number;
  traite_count: number;
}

export interface BulkEmailValidationSyncItem {
  project_id: string;
  email_is_valid?: boolean | null;
}

export interface BulkEmailValidationSyncResponse {
  message: string;
  requested_count: number;
  updated_count: number;
  items: BulkEmailValidationSyncItem[];
}

export interface BulkStepOneEmailResponse {
  message: string;
  requested_count: number;
  sent_count: number;
  failed_count: number;
  skipped_no_email_count: number;
  not_found_count: number;
  sent_project_ids: string[];
  failed_project_ids: string[];
  skipped_no_email_project_ids: string[];
  not_found_project_ids: string[];
}

export interface BulkResendDevisEmailResponse {
  message: string;
  requested_count: number;
  sent_count: number;
  failed_count: number;
  skipped_no_email_count: number;
  not_eligible_count: number;
  not_found_in_gmail_count: number;
  duplicate_count: number;
  not_found_count: number;
  sent_project_ids: string[];
  failed_project_ids: string[];
  skipped_no_email_project_ids: string[];
  not_eligible_project_ids: string[];
  not_found_in_gmail_project_ids: string[];
  duplicate_project_ids: string[];
  not_found_project_ids: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ProjectHistoryService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/project-history`;

  constructor(private http: HttpClient) {}

  /**
   * Créer les headers d'authentification
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Récupère la liste des projets de l'utilisateur connecté avec filtres
   */
  getProjectHistory(
    limit: number = 10,
    offset: number = 0,
    filters?: ProjectHistoryFilters
  ): Observable<PaginatedProjectResponse> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    
    // Ajouter les filtres aux paramètres
    if (filters) {
      if (filters.origine) {
        params = params.set('origine', filters.origine);
      }
      if (filters.type_projet) {
        params = params.set('type_projet', filters.type_projet);
      }
      if (filters.user_id) {
        params = params.set('user_id', filters.user_id.toString());
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.email_validity) {
        params = params.set('email_validity', filters.email_validity);
      }
      if (filters.cotisation_level) {
        params = params.set('cotisation_level', filters.cotisation_level);
      }
      if (filters.start_date) {
        params = params.set('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params = params.set('end_date', filters.end_date);
      }
    }
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<PaginatedProjectResponse>(this.apiUrl, { 
      params, 
      headers 
    });
  }

  /**
   * Récupère tous les projets avec filtres (admin seulement)
   */
  getAllProjectsWithFilters(
    limit: number = 10,
    offset: number = 0,
    filters?: ProjectHistoryFilters
  ): Observable<PaginatedProjectResponse> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    
    // Ajouter les filtres aux paramètres
    if (filters) {
      if (filters.origine) {
        params = params.set('origine', filters.origine);
      }
      if (filters.type_projet) {
        params = params.set('type_projet', filters.type_projet);
      }
      if (filters.user_id) {
        params = params.set('user_id', filters.user_id.toString());
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.status) {
        params = params.set('status', filters.status);
      }
      if (filters.email_validity) {
        params = params.set('email_validity', filters.email_validity);
      }
      if (filters.cotisation_level) {
        params = params.set('cotisation_level', filters.cotisation_level);
      }
      if (filters.start_date) {
        params = params.set('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params = params.set('end_date', filters.end_date);
      }
    }
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<PaginatedProjectResponse>(`${this.apiUrl}/admin/all`, { 
      params, 
      headers 
    });
  }

  /**
   * Récupère la liste simple des projets (fallback)
   */
  getProjectHistorySimple(limit: number = 5, offset: number = 0): Observable<ProjectHistoryListItem[]> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<ProjectHistoryListItem[]>(this.apiUrl, { 
      params, 
      headers 
    });
  }

  /**
   * Récupère un projet spécifique par son project_id (string) avec tous les détails
   */
  getProjectById(project_id: string): Observable<ProjectHistoryItem> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<ProjectHistoryItem>(`${this.apiUrl}/${project_id}`, { 
      headers 
    });
  }

  /**
   * Supprime un projet de l'historique par son project_id (string)
   */
  deleteProject(project_id: string): Observable<{ message: string; project_id: string }> {
    const headers = this.getAuthHeaders();
    
    return this.http.delete<{ message: string; project_id: string }>(`${this.apiUrl}/${project_id}`, { 
      headers 
    });
  }

  /**
   * Met à jour le statut d'un projet
   */
  updateProjectStatus(project_id: string, status: string): Observable<{ message: string; project_id: string; previous_status: string; status: string }> {
    const headers = this.getAuthHeaders();

    return this.http.patch<{ message: string; project_id: string; previous_status: string; status: string }>(
      `${this.apiUrl}/${project_id}/status`,
      { status },
      { headers }
    );
  }

  /**
   * Met à jour le statut de plusieurs projets
   */
  updateProjectsStatus(project_ids: string[], status: string): Observable<BulkStatusProjectsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.patch<BulkStatusProjectsResponse>(`${this.apiUrl}/bulk-status`, {
      project_ids,
      status
    }, {
      headers
    });
  }

  /**
   * Supprime plusieurs projets de l'historique en une seule requête
   */
  deleteProjects(project_ids: string[]): Observable<BulkDeleteProjectsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkDeleteProjectsResponse>(`${this.apiUrl}/bulk-delete`, {
      project_ids
    }, {
      headers
    });
  }

  /**
   * Duplique un projet existant (origine devient 'Doublon')
   */
  duplicateProject(project_id: string): Observable<{ message: string; original_project_id: string; new_project_id: string; origine: string }> {
    const headers = this.getAuthHeaders();
    
    return this.http.post<{ message: string; original_project_id: string; new_project_id: string; origine: string }>(`${this.apiUrl}/${project_id}/duplicate`, {}, { 
      headers 
    });
  }

  /**
   * Récupère tous les doublons créés à partir d'un projet
   */
  getProjectDuplicates(project_id: string): Observable<{ original_project_id: string; duplicates_count: number; duplicates: any[] }> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<{ original_project_id: string; duplicates_count: number; duplicates: any[] }>(`${this.apiUrl}/${project_id}/duplicates`, { 
      headers 
    });
  }

  /**
   * Récupère le nombre total de projets pour l'utilisateur
   */
  getTotalProjectsCount(): Observable<{count: number}> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<{count: number}>(`${this.apiUrl}/count`, { 
      headers 
    });
  }

  /**
   * Exporte l'historique des projets en fichier Excel.
   */
  exportProjectHistoryExcel(projectIds: string[] = [], options: ProjectHistoryExportOptions = {}): Observable<HttpResponse<Blob>> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.post(`${this.apiUrl}/export/excel`, {
      project_ids: projectIds,
      ...options
    }, {
      headers,
      observe: 'response',
      responseType: 'blob'
    });
  }

  /**
   * Recherche dans l'historique des projets
   */
  searchProjects(query: string, limit: number = 10): Observable<ProjectHistoryListItem[]> {
    const params = new HttpParams()
      .set('query', query)
      .set('limit', limit.toString());
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<ProjectHistoryListItem[]>(`${this.apiUrl}/search/`, { 
      params, 
      headers 
    });
  }

  /**
   * Récupère les statistiques de l'utilisateur
   */
  getUserStats(): Observable<ProjectStats> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<ProjectStats>(`${this.apiUrl}/stats/user`, { 
      headers 
    });
  }

  /**
   * Récupère les compteurs de projets par utilisateur et par statut.
   */
  getProjectUserStatusCounts(): Observable<ProjectUserStatusCounts[]> {
    const headers = this.getAuthHeaders();

    return this.http.get<ProjectUserStatusCounts[]>(`${this.apiUrl}/users/status-counts`, {
      headers
    });
  }

  // Méthodes admin (si nécessaire)
  
  /**
   * Récupère tous les projets (admin seulement)
   */
  getAllProjectsAdmin(limit: number = 50, offset: number = 0): Observable<PaginatedProjectResponse> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    
    const headers = this.getAuthHeaders();
    
    return this.http.get<PaginatedProjectResponse>(`${this.apiUrl}/admin/all`, { 
      params, 
      headers 
    });
  }

  /**
   * Supprime un projet (admin seulement) par project_id (string)
   */
  deleteProjectAdmin(project_id: string): Observable<{ message: string; project_id: string }> {
    const headers = this.getAuthHeaders();
    
    return this.http.delete<{ message: string; project_id: string }>(`${this.apiUrl}/admin/${project_id}`, { 
      headers 
    });
  }

  /**
   * Met à jour le statut d'un projet (admin)
   */
  updateProjectStatusAdmin(project_id: string, status: string): Observable<{ message: string; project_id: string; previous_status: string; status: string }> {
    const headers = this.getAuthHeaders();

    return this.http.patch<{ message: string; project_id: string; previous_status: string; status: string }>(
      `${this.apiUrl}/admin/${project_id}/status`,
      { status },
      { headers }
    );
  }

  /**
   * Met à jour le statut de plusieurs projets (admin)
   */
  updateProjectsStatusAdmin(project_ids: string[], status: string): Observable<BulkStatusProjectsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.patch<BulkStatusProjectsResponse>(`${this.apiUrl}/admin/bulk-status`, {
      project_ids,
      status
    }, {
      headers
    });
  }

  /**
   * Supprime plusieurs projets (admin seulement)
   */
  deleteProjectsAdmin(project_ids: string[]): Observable<BulkDeleteProjectsResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkDeleteProjectsResponse>(`${this.apiUrl}/admin/bulk-delete`, {
      project_ids
    }, {
      headers
    });
  }

  deleteProjectsByMobileExcel(file: File): Observable<BulkDeleteByMobileExcelResponse> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<BulkDeleteByMobileExcelResponse>(
      `${this.apiUrl}/bulk-delete-by-mobile-excel`,
      formData,
      { headers }
    );
  }

  /**
   * Récupère les statistiques admin
   */
  getAdminStats(): Observable<any> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<any>(`${this.apiUrl}/admin/stats`, { 
      headers 
    });
  }

  /**
   * Récupère la liste des utilisateurs actifs pour attribution
   */
  getActiveUsers(): Observable<any[]> {
    const headers = this.getAuthHeaders();
    
    return this.http.get<any[]>(`${this.apiUrl}/users/active`, { 
      headers 
    });
  }

  /**
   * Attribue des projets à un autre utilisateur
   */
  assignProjectsToUser(assignmentData: {
    project_ids: string[],
    target_user_id: number,
    keep_copy: boolean,
    assignment_reason?: string
  }): Observable<any> {
    const headers = this.getAuthHeaders();
    
    return this.http.post<any>(`${this.apiUrl}/assign-projects`, assignmentData, { 
      headers 
    });
  }

  /**
   * Récupère les projets attribués à l'utilisateur actuel
   */
  getProjectsAssignedToMe(limit: number = 100, offset: number = 0): Observable<any[]> {
    const headers = this.getAuthHeaders();
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    
    return this.http.get<any[]>(`${this.apiUrl}/assigned-to-me`, { 
      headers,
      params 
    });
  }

  syncEmailValidation(project_ids: string[], only_missing: boolean = true): Observable<BulkEmailValidationSyncResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkEmailValidationSyncResponse>(`${this.apiUrl}/email-validation/sync`, {
      project_ids,
      only_missing
    }, {
      headers
    });
  }

  /**
   * Envoie l'email de demande d'information (step one) aux projets sélectionnés.
   */
  sendBulkStepOneEmail(project_ids: string[]): Observable<BulkStepOneEmailResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkStepOneEmailResponse>(`${this.apiUrl}/bulk-step-one-email`, {
      project_ids
    }, {
      headers
    });
  }

  /**
   * Envoie l'email d'économies (step one) aux projets sélectionnés.
   */
  sendBulkStepOneSavingsEmail(project_ids: string[]): Observable<BulkStepOneEmailResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkStepOneEmailResponse>(`${this.apiUrl}/bulk-step-one-savings-email`, {
      project_ids
    }, {
      headers
    });
  }

  /**
   * Renvoie le mail devis déjà envoyé (depuis Gmail) aux projets traités sélectionnés.
   */
  bulkResendDevisEmail(project_ids: string[]): Observable<BulkResendDevisEmailResponse> {
    const headers = this.getAuthHeaders();

    return this.http.post<BulkResendDevisEmailResponse>(`${this.apiUrl}/bulk-resend-devis-email`, {
      project_ids
    }, {
      headers
    });
  }

  /**
   * Importe des projets historiques depuis un fichier CSV OGGO.
   */
  importProjectsCsv(file: File): Observable<{ message: string; imported: number; skipped: number; doublons: number; errors: string[] }> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{ message: string; imported: number; skipped: number; doublons: number; errors: string[] }>(
      `${this.apiUrl}/import-csv`,
      formData,
      { headers }
    );
  }
}
